import { createSelector } from 'reselect'
import {
  networkGroup,
  params,
} from './utils'

import frontendFormUtils from 'components/form/utils'
import SlotModel from 'shared/src/slotModel'
import formUtils from 'shared/src/formUtils'
import Template from 'shared/src/template'
import bookingformSelectors from './bookingform'

import {
  getReservationPaymentStatus,
} from '../../utils/reservation'

const calendar = state => state.booking.calendar
const config = state => state.booking.config
const condensedDates = state => state.booking.condensedDates
const currentBookingform = state => state.booking.currentBookingform
const reservation = state => state.booking.reservation
const reservationRedirect = state => state.booking.reservationRedirect
const slot = state => state.booking.slot
const options = state => state.booking.options
const details = state => state.booking.details
const recaptchaKey = state => state.booking.recaptchaKey
const reservedBookingToken = state => state.booking.reservedBookingToken
const stripeBookingToken = state => state.booking.stripeBookingToken
const reservationError = state => state.booking.reservationError
const embedHTML = state => state.booking.embedHTML

const reservationPaymentStatus = createSelector(
  reservation,
  (reservation) => getReservationPaymentStatus(reservation),
)

const slotFormConfig = createSelector(
  config,
  (config) => config
)

const calendarConfigRaw = createSelector(
  config,
  (config) => {
    return config && config.calendar_config ? config.calendar_config : null
  }
)

const calendarConfig = createSelector(
  config,
  (config) => {
    return config && config.calendar_config ? config.calendar_config : {}
  }
)

const bookingform = createSelector(
  bookingformSelectors.bookingForms,
  params,
  (data, params) => data.find(form => form.url == params.bookingform || form.id == params.bookingform)
)

const optionsSchema = createSelector(
  slotFormConfig,
  slot,
  (config, slot) => {
    const template = Template({
      slot,
      config,
    })
    return formUtils.getOptionsSchema({
      config,
      slot,
      template,
    })
  }
)

const optionsSchemaNamespaced = createSelector(
  optionsSchema,
  (schema) => schema.map(field => {
    if(typeof(field) == 'string') return field
    return Object.assign({}, field, {
      id: `options.${field.id}`,
    })
  })
)

const detailsSchema = createSelector(
  slotFormConfig,
  slot,
  (config, slot) => {
    const template = Template({
      slot,
      config,
    })
    return formUtils.getDetailsSchema({
      config,
      slot,
      template,
    })
  }
)

const detailsSchemaNamespaced = createSelector(
  detailsSchema,
  (schema) => schema.map(field => {
    if(typeof(field) == 'string') return field
    return Object.assign({}, field, {
      id: `details.${field.id}`,
    })
  })
)

const optionsValues = createSelector(
  optionsSchema,
  options,
  (schema, values) => {
    return frontendFormUtils.getInitialValues(schema, values)
  }
)

const detailsValues = createSelector(
  detailsSchema,
  details,
  (schema, values) => {
    return frontendFormUtils.getInitialValues(schema, values)
  }
)

const optionsTotals = () => createSelector(
  slotFormConfig,
  slot,
  (_, bookingData) => bookingData,
  (config, slot, bookingData) => {
    const slotModel = SlotModel({
      config,
      slot,
      booking: {
        data: bookingData,
      }
    })

    const totals = slotModel.getTotals()
    const bookingFee = slotModel.getBookingFee()

    const rows = totals.rows.concat([{
      short_title: 'Booking Fee',
      amount: 0,
      total: bookingFee,
    }])

    return {
      rows,
      total: totals.total + bookingFee,
      paynow: slotModel.getPayNow(),
    }
  }
)

// these methods depend on the following route params:
//  * date
//  * slot_id
const editingDay = createSelector(
  params,
  calendar,
  (params, calendar) => {
    const {
      date,
    } = params
    return calendar.find(day => day.date == date)
  },
)

const editingSlot = createSelector(
  params,
  editingDay,
  (params, day) => {
    const {
      block_id,
      slot_id,
    } = params
    if(!day) return
    const block = day.blocks.find(b => b.id == block_id)
    if(!block) return
    return block.slots.find(s => s.id == slot_id)
  },
)

const editingBooking = createSelector(
  params,
  editingSlot,
  (params, slot) => {
    const {
      booking_id,
    } = params
    if(booking_id == 'new') return null
    if(!slot) return null
    return slot.bookingRecords.find(booking => booking.id == booking_id)
  },
)

const slotOverrideFormSchema = createSelector(
  slotFormConfig,
  (formConfig) => {
    if(!formConfig || !formConfig.slot_config || !formConfig.slot_config.edit) {
      return []
    }
    return formUtils.processPlainSchema(formConfig.slot_config.edit || [])
  }
)

const daySlotOverrideFormSchema = createSelector(
  slotOverrideFormSchema,
  (schema) => {
    return ([{
      id: 'name',
      title: 'Section Header',
      helperText: 'A title to put above this slot - can be left blank',
    }]).concat(schema)
  }
)

const slotOverrideForm = createSelector(
  slotOverrideFormSchema,
  slot,
  (schema, slot) => {
    if(!slot) {
      return {
        schema,
        data: {},
      }
    }
    const data = schema.reduce((all, field) => {
      all[field.id] = slot[field.id]
      return all
    }, {})
    return {
      schema,
      data,
    }
  }
)

const selectors = {
  recaptchaKey,
  reservedBookingToken,
  reservation,
  reservationRedirect,
  reservationError,
  reservationPaymentStatus,
  calendar,
  bookingform,
  config,
  condensedDates,
  currentBookingform,
  optionsSchema,
  optionsSchemaNamespaced,
  optionsValues,
  optionsTotals,
  detailsSchema,
  stripeBookingToken,
  detailsSchemaNamespaced,
  detailsValues,
  slot,
  slotFormConfig,
  calendarConfigRaw,
  calendarConfig,
  options,
  details,
  editingDay,
  editingSlot,
  editingBooking,
  daySlotOverrideFormSchema,
  slotOverrideFormSchema,
  slotOverrideForm,
  embedHTML,
  ...networkGroup('booking', [
    'loadProject',
    'loadCalendar',
    'loadConfig',
    'pay',
    'reserve',
  ])
}

export default selectors
